import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { clearCredentials } from "../slices/authSlice";
import { AUTH_URL } from "../../constants/urls";
import { ENV } from "../../constants/environment";
import { AUTH_TOKEN_KEY } from "../../constants/appConstants";

const baseQuery = fetchBaseQuery({
    baseUrl: ENV.apiUrl,
    prepareHeaders: (headers, { getState, endpoint }) => {
        const token = localStorage.getItem(AUTH_TOKEN_KEY);
        if (token) {
            headers.set('Authorization', `Bearer ${token}`)
        }
        headers.set('X-Source-Module', 'Web');

        const os =
            (typeof navigator !== 'undefined' && (navigator.userAgentData?.platform || navigator.platform || navigator.userAgent))
            || 'Unknown';
        headers.set('X-Client-OS', os);
        return headers;
    },
    fetchOptions: {
        // HTTPS localhost için SSL sertifikası doğrulamasını atla (sadece development için)
        ...(ENV.apiUrl?.includes('localhost') && {
            // Node.js'de bu ayarlar çalışmaz, ama tarayıcı için gerekli değil
        })
    }
});

// API bağlantı hatalarını takip et
let consecutiveNetworkErrors = 0;
let lastNetworkErrorTime = 0;
const NETWORK_ERROR_THRESHOLD = 3; // 3 ardışık hata sonrası uyarı göster
const NETWORK_ERROR_RESET_TIME = 30000; // 30 saniye sonra sayacı sıfırla

const SILENT_ENDPOINTS = new Set(["getUnreadTotal", "getUnreadCountsByRoom"]);

// Timeout handling ile base query'yi wrap et
const baseQueryWithTimeout = async (args, api, extraOptions) => {
    const shouldLog =
        process.env.REACT_APP_DEBUG_API === "true" && !SILENT_ENDPOINTS.has(api?.endpoint);
    const getCallerFromStack = () => {
        try {
            const stack = new Error().stack;
            if (!stack) return undefined;
            const lines = stack.split('\n').map(l => l.trim());
            const line = lines.find(l => l.includes('/src/') && !l.includes('apiSlice.js') && !l.includes('node_modules'));
            return line;
        } catch {
            return undefined;
        }
    };
    const normalizeArgs = (a) => {
        if (typeof a === 'string') return { url: a, method: 'GET' };
        return { method: a.method || 'GET', url: a.url, params: a.params, body: a.body };
    };
    const req = normalizeArgs(args);
    const baseUrl = ENV.apiUrl || '';
    const isAbsolute = typeof req.url === 'string' && /^(http|https):\/\//.test(req.url);
    const fullUrl = isAbsolute ? req.url : `${baseUrl}${req.url?.startsWith('/') ? req.url : `/${req.url || ''}`}`;
    const caller = getCallerFromStack();
    const start = Date.now();
    if (shouldLog) {
        const purposeLabel = api?.endpoint || `${req.method} ${req.url}`;
        // eslint-disable-next-line no-console
        console.log(`📡 [${purposeLabel} Request]`, {
            endpoint: api?.endpoint,
            method: req.method,
            url: req.url,
            fullUrl,
            params: req.params,
            body: req.body,
            caller,
        });
    }
    if (api?.endpoint === "getDoctorsByStatus") {
    }
    try {
        // SKRS ICD-10 / ilaç listeleri ilk çekimde çok uzun sürebilir (sayfalama + dosya yazma)
        const longRunningEndpoints = new Set(["getIcd10Codes", "getIlacCodes", "sendBroadcastNotification"]);
        const timeoutMs = longRunningEndpoints.has(api?.endpoint) ? 15 * 60 * 1000 : 60000;
        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Request timeout')), timeoutMs);
        });
        
        const queryPromise = baseQuery(args, api, extraOptions);
        let result = await Promise.race([queryPromise, timeoutPromise]);
        const durationMs = Date.now() - start;
        
        // Başarılı istek sonrası network error sayacını sıfırla
        if (!result?.error) {
            consecutiveNetworkErrors = 0;
        }
        
        if (shouldLog) {
            const purposeLabel = api?.endpoint || `${req.method} ${req.url}`;
            const status = result?.error?.status || 200;
            const isSuccess = !result?.error;
            const emoji = isSuccess ? '✅' : '❌';
            // eslint-disable-next-line no-console
            console.log(`${emoji} [${purposeLabel} Response]`, {
                endpoint: api?.endpoint,
                method: req.method,
                url: req.url,
                fullUrl,
                ok: isSuccess,
                status: status,
                error: result?.error,
                data: result?.data,
                durationMs,
            });
        }
        if (api?.endpoint === "getDoctorsByStatus") {
        }
        
        // Network hatalarını kontrol et (FETCH_ERROR)
        if (result?.error && result.error.status === 'FETCH_ERROR') {
            const now = Date.now();
            
            // Eğer son hatadan 30 saniye geçtiyse sayacı sıfırla
            if (now - lastNetworkErrorTime > NETWORK_ERROR_RESET_TIME) {
                consecutiveNetworkErrors = 0;
            }
            
            consecutiveNetworkErrors++;
            lastNetworkErrorTime = now;
            
            // Sadece belirli sayıda ardışık hata sonrası kullanıcıya bildir
            // Bu sayede her istekte toast göstermek yerine, sadece kritik durumlarda gösteririz
            if (consecutiveNetworkErrors >= NETWORK_ERROR_THRESHOLD) {
                // Global error handler'a bildir (App.js'de dinlenecek)
                window.dispatchEvent(new CustomEvent('api-connection-error', {
                    detail: {
                        message: 'API bağlantısı kurulamadı. Lütfen internet bağlantınızı kontrol edin.',
                        error: result.error
                    }
                }));
            }
            
            // Hata mesajını daha kullanıcı dostu hale getir
            result.error.data = {
                message: 'Sunucuya bağlanılamıyor. Lütfen internet bağlantınızı kontrol edin ve tekrar deneyin.',
                originalError: result.error.error
            };
        }
        // Eğer 401 döndüyse refresh token dene
        if (result?.error?.status === 401) {
            // Randevu, Issue ve Language endpoint'leri için clearCredentials() çağrılmasını engelle
            // Bu endpoint'ler asistan için henüz desteklenmiyor olabilir veya 401 beklenen bir durumdur.
            // Language: 401 alındığında çıkış yaptırmayız; sayfa hata gösterir.
            const requestUrl = (req.url || "").toLowerCase();
            const isAppointmentEndpoint = requestUrl.includes('/availableappointmentday') ||
                                         requestUrl.includes('/bookedappointment');
            const isIssueEndpoint = requestUrl.includes('/issue');
            const isLanguageEndpoint = requestUrl.includes('/language');
            const isEnabizEndpoint =
                requestUrl.includes('/enabiz') ||
                requestUrl.includes('/hastakayitsilme');
            const isCareTeamEndpoint = requestUrl.includes('/careteam');
            const skipClearOn401 = isAppointmentEndpoint || isIssueEndpoint || isLanguageEndpoint || isEnabizEndpoint || isCareTeamEndpoint;
            
            console.log('🔄 [API] 401 hatası, refresh token deneniyor...', { 
                endpoint: api?.endpoint, 
                url: req.url,
                skipClearOn401 
            });
            
            try {
                const refreshResult = await baseQuery({ url: `${AUTH_URL}/refreshToken`, method: 'GET' }, api, extraOptions);
                if (refreshResult?.data?.token) {
                    console.log('✅ [API] Refresh token başarılı, yeni token kaydediliyor...');
                    localStorage.setItem(AUTH_TOKEN_KEY, refreshResult.data.token);
                    // Orijinal isteği yeni token ile tekrar dene
                    result = await baseQuery(args, api, extraOptions);
                    if (shouldLog) {
                        const isRetrySuccess = !result?.error;
                        const retryEmoji = isRetrySuccess ? '✅' : '❌';
                        // eslint-disable-next-line no-console
                        console.log(`${retryEmoji} [API Retry After Refresh]`, {
                            endpoint: api?.endpoint,
                            method: req.method,
                            url: req.url,
                            fullUrl,
                            ok: isRetrySuccess,
                            status: result?.error?.status || 200,
                            error: result?.error,
                            data: result?.data,
                        });
                    }
                } else {
                    console.log('❌ [API] Refresh token başarısız');
                    if (!skipClearOn401) {
                        console.log('❌ [API] Credentials temizleniyor (non-appointment/issue endpoint)...');
                        api.dispatch(clearCredentials());
                    } else {
                        console.log('⚠️ [API] Randevu/Issue endpoint için credentials korunuyor (401 beklenen bir durum olabilir)');
                    }
                }
            } catch (e) {
                console.log('💥 [API] Refresh token hatası:', e);
                if (!skipClearOn401) {
                    console.log('❌ [API] Credentials temizleniyor (non-appointment/issue endpoint)...');
                    api.dispatch(clearCredentials());
                } else {
                    console.log('⚠️ [API] Randevu/Issue endpoint için credentials korunuyor (401 beklenen bir durum olabilir)');
                }
            }
        }
        return result;
    } catch (error) {
        // Timeout hatası
        if (error.message === 'Request timeout') {
            const timeoutError = {
                status: 'TIMEOUT',
                error: 'Request timeout',
                data: {
                    message: 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.'
                }
            };
            
            // Global error handler'a bildir
            window.dispatchEvent(new CustomEvent('api-connection-error', {
                detail: {
                    message: 'İstek zaman aşımına uğradı. Lütfen tekrar deneyin.',
                    error: timeoutError
                }
            }));
            
            if (api?.endpoint === "getDoctorsByStatus") {
            }
            return { error: timeoutError };
        }
        
        if (shouldLog) {
            // eslint-disable-next-line no-console
            console.log('💥 [API Exception]', {
                endpoint: api?.endpoint,
                method: req.method,
                url: req.url,
                fullUrl,
                error,
            });
        }
        const fallbackError = {
            status: 'UNKNOWN',
            error: error?.message || 'Unknown error',
            data: {
                message: 'Beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.'
            }
        };
        window.dispatchEvent(new CustomEvent('api-connection-error', {
            detail: {
                message: fallbackError.data.message,
                error: fallbackError
            }
        }));
        return { error: fallbackError };
    }
};

export const apiSlice = createApi({
    baseQuery: baseQueryWithTimeout,
    tagTypes: ['User', 'Role', 'Doctor', 'MessageRoomUnread', 'MessageRoomUnreadTotal', 'PreferredLanguage', 'Notification', 'Triage', 'Title', 'ServicePackage'],
    endpoints: (builder) => ({})
});